import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

// User Registration Trend Chart
const UserRegistrationChart = ({ data }) => {
  const chartData = {
    labels: data.map(item => item.date),
    datasets: [
      {
        label: 'New Users',
        data: data.map(item => item.count),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: true
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: true,
        text: 'User Registration Trends',
        font: {
          size: 16,
          weight: 'bold'
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  };

  return (
    <div style={{ height: '300px' }}>
      <Line data={chartData} options={options} />
    </div>
  );
};

// Submissions Trend Chart
const SubmissionsChart = ({ data }) => {
  const chartData = {
    labels: data.map(item => item.date),
    datasets: [
      {
        label: 'Daily Submissions',
        data: data.map(item => item.count),
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.4,
        fill: true
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: true,
        text: 'Daily Test Submissions',
        font: {
          size: 16,
          weight: 'bold'
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  return (
    <div style={{ height: '300px' }}>
      <Line data={chartData} options={options} />
    </div>
  );
};

// Test Categories Chart
const TestCategoriesChart = ({ tests }) => {
  const categoryCounts = tests.reduce((acc, test) => {
    acc[test.category] = (acc[test.category] || 0) + 1;
    return acc;
  }, {});

  const chartData = {
    labels: Object.keys(categoryCounts),
    datasets: [
      {
        label: 'Number of Tests',
        data: Object.values(categoryCounts),
        backgroundColor: [
          '#3B82F6',
          '#10B981',
          '#F59E0B',
          '#EF4444',
          '#8B5CF6',
          '#06B6D4',
          '#84CC16',
          '#F97316'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true
        }
      },
      title: {
        display: true,
        text: 'Test Categories Distribution',
        font: {
          size: 16,
          weight: 'bold'
        }
      }
    }
  };

  return (
    <div style={{ height: '300px' }}>
      <Pie data={chartData} options={options} />
    </div>
  );
};

// Performance Distribution Chart
const PerformanceDistributionChart = ({ submissions }) => {
  const accuracyRanges = {
    '0-60%': 0,
    '61-70%': 0,
    '71-80%': 0,
    '81-90%': 0,
    '91-100%': 0
  };

  submissions.forEach(submission => {
    const accuracy = submission.accuracy;
    if (accuracy <= 60) accuracyRanges['0-60%']++;
    else if (accuracy <= 70) accuracyRanges['61-70%']++;
    else if (accuracy <= 80) accuracyRanges['71-80%']++;
    else if (accuracy <= 90) accuracyRanges['81-90%']++;
    else accuracyRanges['91-100%']++;
  });

  const chartData = {
    labels: Object.keys(accuracyRanges),
    datasets: [
      {
        label: 'Number of Attempts',
        data: Object.values(accuracyRanges),
        backgroundColor: [
          '#DC2626', // Red for 0-60%
          '#F59E0B', // Amber for 61-70%
          '#EAB308', // Yellow for 71-80%
          '#84CC16', // Lime for 81-90%
          '#10B981'  // Green for 91-100%
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: true,
        text: 'Accuracy Distribution',
        font: {
          size: 16,
          weight: 'bold'
        }
      }
    }
  };

  return (
    <div style={{ height: '300px' }}>
      <Doughnut data={chartData} options={options} />
    </div>
  );
};

// Top Tests Performance Chart
const TopTestsPerformanceChart = ({ tests }) => {
  const chartData = {
    labels: tests.slice(0, 10).map(test => test.name.length > 15 ? test.name.substring(0, 15) + '...' : test.name),
    datasets: [
      {
        label: 'Average Accuracy',
        data: tests.slice(0, 10).map(test => test.avgAccuracy),
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 1
      },
      {
        label: 'Number of Attempts',
        data: tests.slice(0, 10).map(test => test.attempts),
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
        borderColor: 'rgb(16, 185, 129)',
        borderWidth: 1,
        yAxisID: 'y1'
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Top 10 Tests - Performance vs Attempts',
        font: {
          size: 16,
          weight: 'bold'
        }
      }
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Accuracy (%)'
        },
        min: 0,
        max: 100
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Number of Attempts'
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    }
  };

  return (
    <div style={{ height: '400px' }}>
      <Bar data={chartData} options={options} />
    </div>
  );
};

// User Types Chart
const UserTypesChart = ({ totalUsers, premiumUsers }) => {
  const regularUsers = totalUsers - premiumUsers;
  
  const chartData = {
    labels: ['Regular Users', 'Premium Users'],
    datasets: [
      {
        data: [regularUsers, premiumUsers],
        backgroundColor: ['#6B7280', '#F59E0B'],
        borderWidth: 2,
        borderColor: '#fff'
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true
        }
      },
      title: {
        display: true,
        text: 'User Types Distribution',
        font: {
          size: 16,
          weight: 'bold'
        }
      }
    }
  };

  return (
    <div style={{ height: '300px' }}>
      <Doughnut data={chartData} options={options} />
    </div>
  );
};

export {
  UserRegistrationChart,
  SubmissionsChart,
  TestCategoriesChart,
  PerformanceDistributionChart,
  TopTestsPerformanceChart,
  UserTypesChart
};
